import React, { forwardRef, useCallback, useContext } from 'react';
import { FormattedMessage } from 'react-intl';
import ListItemIcon from '@material-ui/core/ListItemIcon';
import MenuItem from '@material-ui/core/MenuItem';
import Lock from '@material-ui/icons/Lock';
import LockOpen from '@material-ui/icons/LockOpen';
import { PromptContext } from '@eas/common-web';
import { workOrderBulkApi } from '@modules/planner/calendar/controls/bulk-operations/bulk-operations-api';
import { WorkOrder } from '@models';

type MenuItemProps = {
  closeMenu: () => void;
  workOrder: WorkOrder;
  afterSubmit: () => void;
};

export const useHandleLockWorkOrder = (
  workOrder: WorkOrder,
  afterSubmit: () => void,
  closeMenu: () => void
) =>
  useCallback(async () => {
    if (workOrder.locked) {
      await workOrderBulkApi.unlock([workOrder.id]);
    } else {
      await workOrderBulkApi.lock([workOrder.id]);
    }

    afterSubmit();

    closeMenu();
  }, [workOrder.locked, workOrder.id, afterSubmit, closeMenu]);

export const LockWorkOrderMenuItem = forwardRef<HTMLLIElement, MenuItemProps>(
  function LockWorkOrderMenuItem({ closeMenu, workOrder, afterSubmit }, ref) {
    const { testPrompt } = useContext(PromptContext);
    const handleLock = useHandleLockWorkOrder(
      workOrder,
      afterSubmit,
      closeMenu
    );

    // https://jira.zsdis.sk/browse/FSMS-946
    if (workOrder.currentState?.code !== 'NAP') return null;

    return (
      <MenuItem
        ref={ref}
        onClick={() => {
          testPrompt({
            key: `WORK_ORDER_LOCK_ALERT_${workOrder.id}`,
            submitCallback: handleLock,
          });
        }}
      >
        <ListItemIcon>
          {workOrder.locked ? (
            <LockOpen fontSize="small" />
          ) : (
            <Lock fontSize="small" />
          )}
        </ListItemIcon>
        {workOrder.locked ? (
          <FormattedMessage
            id="WORK_ORDER_MENU_ITEM_UNLOCK"
            defaultMessage="Odomknúť"
          />
        ) : (
          <FormattedMessage
            id="WORK_ORDER_MENU_ITEM_LOCK"
            defaultMessage="Zamknúť"
          />
        )}
      </MenuItem>
    );
  }
);
